import { IEditServiceButtonFeatureViewModel } from '@shared/types/mobx/features/osiAccruals';
import { makeAutoObservable } from 'mobx';
import { notifications } from '@shared/utils/notifications';
import { inject, injectable } from 'inversify';
import { EditServiceFormValues, OsiServiceResponse } from '@shared/types/osi/services';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { queryClient } from '@shared/api/reactQuery';
import {
  getPostApiOsiServicesCreateAccuralsCurrentPlanMutationOptions,
  getPutApiOsiServicesIdMutationOptions
} from '@shared/api/orval/osi-services/osi-services';
import { type RequestsOsiServiceRequest } from '@shared/api/orval/models/requestsOsiServiceRequest';
import type { ApiResponseInt32SystemPrivateCoreLib } from '@shared/api/orval/models/apiResponseInt32SystemPrivateCoreLib';

@injectable()
export class EditButtonFeatureViewModel implements IEditServiceButtonFeatureViewModel {
  isDialogOpen = false;

  hookForm: any = null;

  isLoading = false;

  // Force update confirmation dialog
  isForceUpdateConfirmDialogOpen = false;

  forceUpdatePayload: RequestsOsiServiceRequest | null = null;

  forceUpdateMessage: string | null = null;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  closeDialog = (): void => {
    this.isDialogOpen = false;
    this.hookForm?.reset();
  };

  closeForceUpdateConfirmDialog = (): void => {
    this.isForceUpdateConfirmDialogOpen = false;
    this.forceUpdatePayload = null;
    this.forceUpdateMessage = null;
  };

  editService = async (values: EditServiceFormValues & { serviceId: number }): Promise<void> => {
    if (!this.osiModule.osiId && this.osiModule.osiId !== 0) return;

    this.isLoading = true;

    try {
      const payload: RequestsOsiServiceRequest = {
        osiId: this.osiModule.osiId,
        nameRu: values.service.nameRu,
        nameKz: values.service.nameKz,
        serviceGroupId: values.serviceGroupId,
        accuralMethodId: values.accrualMethodId,
        amount: values.amount,
        // Фаза 3: передаём AllowFines и TotalMonths
        allowFines: values.allowFines,
        totalMonths: values.totalMonths,
        // Ежемесячное подтверждение
        requiresMonthlyConfirmation: values.requiresMonthlyConfirmation
      };

      const mutation = queryClient.getMutationCache().build(
        queryClient,
        getPutApiOsiServicesIdMutationOptions({
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const response = await mutation.execute({
        id: values.serviceId,
        data: payload
      });

      // If code is 100, show confirmation dialog
      if (response && response.code === 100) {
        this.forceUpdatePayload = {
          ...payload,
          forceUpdate: true
        };
        this.forceUpdateMessage = response.message ?? 'Требуется подтверждение для обновления';
        this.isForceUpdateConfirmDialogOpen = true;
        return;
      }
      if (response && response.code !== 0) {
        // If code !== 0 and code !== 100, show error
        notifications.error(response.message ?? '');
        return;
      }

      this._reloadCb?.();
      this.closeDialog();
    } catch (e) {
      if (e instanceof Error) {
        notifications.error();
        return;
      }
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  // editServiceAndRecreate — поведение «Начислить по услуге»:
  // 1) Сохраняем услугу через тот же PUT, что и обычное «Сохранить» (включая обработку code=100 forceUpdate).
  // 2) Если PUT успешен — вызываем POST /create-accurals-current-plan только для editServiceId.
  // 3) Сообщаем пользователю результат: created>0 → success, created=0 → info (уже начислена).
  editServiceAndRecreate = async (values: EditServiceFormValues & { serviceId: number }): Promise<void> => {
    if (!this.osiModule.osiId && this.osiModule.osiId !== 0) return;

    this.isLoading = true;

    try {
      const payload: RequestsOsiServiceRequest = {
        osiId: this.osiModule.osiId,
        nameRu: values.service.nameRu,
        nameKz: values.service.nameKz,
        serviceGroupId: values.serviceGroupId,
        accuralMethodId: values.accrualMethodId,
        amount: values.amount,
        allowFines: values.allowFines,
        totalMonths: values.totalMonths,
        requiresMonthlyConfirmation: values.requiresMonthlyConfirmation
      };

      // 1) PUT /api/OsiServices/{id}
      const putMutation = queryClient.getMutationCache().build(
        queryClient,
        getPutApiOsiServicesIdMutationOptions({
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const putResponse = await putMutation.execute({
        id: values.serviceId,
        data: payload
      });

      // Тот же flow обработки code=100 как в editService — выходим в подтверждающий диалог.
      // forceUpdate подтверждение пройдёт через confirmForceUpdate (оно делает только PUT, без recreate).
      // Это сознательное упрощение: после force-confirm пользователь при необходимости нажмёт «Начислить по услуге» снова.
      if (putResponse && putResponse.code === 100) {
        this.forceUpdatePayload = {
          ...payload,
          forceUpdate: true
        };
        this.forceUpdateMessage = putResponse.message ?? 'Требуется подтверждение для обновления';
        this.isForceUpdateConfirmDialogOpen = true;
        return;
      }
      if (putResponse && putResponse.code !== 0) {
        notifications.error(putResponse.message ?? '');
        return;
      }

      // 2) POST /api/OsiServices/create-accurals-current-plan по одной услуге.
      const recreateMutation = queryClient
        .getMutationCache()
        .build(queryClient, getPostApiOsiServicesCreateAccuralsCurrentPlanMutationOptions());

      const recreateResponse = (await recreateMutation.execute({
        data: { osiId: this.osiModule.osiId, osiServiceIds: [values.serviceId] }
      })) as ApiResponseInt32SystemPrivateCoreLib | null | undefined;

      if (recreateResponse && recreateResponse.code !== 0) {
        notifications.error(recreateResponse.message ?? '');
        return;
      }

      // 3) Различаем «реально доначислили» и «было уже начислено» (бэк-guard).
      const created = recreateResponse?.result ?? 0;
      if (created > 0) {
        notifications.success('accruals:recreateForServiceSuccess');
      } else {
        notifications.info('accruals:recreateForServiceInfo');
      }

      this._reloadCb?.();
      this.closeDialog();
    } catch (e) {
      if (e instanceof Error) {
        notifications.error();
        return;
      }
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  confirmForceUpdate = async (): Promise<void> => {
    if (!this.forceUpdatePayload) return;

    this.isLoading = true;

    try {
      const mutation = queryClient.getMutationCache().build(
        queryClient,
        getPutApiOsiServicesIdMutationOptions({
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const response = await mutation.execute({
        id: this.hookForm?.getValues('serviceId'),
        data: this.forceUpdatePayload
      });

      // Check response code after retry
      if (response && response.code !== 0) {
        notifications.error(response.message ?? '');
        return;
      }

      this._reloadCb?.();
      this.closeDialog();
      this.closeForceUpdateConfirmDialog();
    } catch (e) {
      if (e instanceof Error) {
        notifications.error();
        return;
      }
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  openDialog = (): void => {
    this.isDialogOpen = true;
  };

  onEditServiceClick = (service: OsiServiceResponse, isCustom: boolean): void => {
    const values = {
      service: {
        code: isCustom ? 'custom' : 'default',
        nameRu: service.nameRu ?? '',
        nameKz: service.nameKz ?? ''
      },
      serviceGroupId: service.serviceGroupId,
      accrualMethodId: service.accuralMethodId,
      amount: service.amount,
      serviceId: service.id,
      // Фаза 3: загружаем текущие значения AllowFines и TotalMonths
      allowFines: service.allowFines ?? true,
      totalMonths: service.totalMonths ?? null,
      // Ежемесячное подтверждение
      requiresMonthlyConfirmation: service.requiresMonthlyConfirmation ?? false
    };

    this.hookForm?.reset(values);
    this.hookForm?.trigger();

    this.openDialog();
  };

  setHookForm = (hookForm: any): void => {
    this.hookForm = hookForm;
  };

  setReloadCb = (cb: any): void => {
    this._reloadCb = cb;
  };

  private _reloadCb: any = () => {};
}
